import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowDown, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import GlowText from '../components/ui/GlowText';
import VideoCard from '../components/home/VideoCard';
import { fetchLatestVideos } from '../services/youtubeService';

const Home = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadVideos = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchLatestVideos();
      if (data && data.length > 0) {
        setVideos(data);
      } else {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const scrollToVideos = () => {
    document.getElementById('videos').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-dark-900">
      <Navbar />

      {/* Cinematic Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-cyan-900/10 rounded-full blur-[140px] animate-pulse-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-blue-900/10 rounded-full blur-[140px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
      </div>

      {/* Hero Section */}
      <section className="relative h-screen flex flex-col justify-center items-center text-center px-6 z-10">
        <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 1.2 }}
           className="space-y-8 max-w-4xl flex flex-col items-center"
        >
          <GlowText as="h1" className="text-6xl md:text-8xl lg:text-9xl mb-2 text-white font-bold tracking-tighter">
            Clarity in Chaos
          </GlowText>
          
          <div className="space-y-4 w-full">
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="text-lg md:text-2xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed tracking-wide"
            >
              Independent analysis, spiritual depth, and global reflection.
              <span className="block mt-2 text-cyan-400/60 italic text-base md:text-lg">Finding signal in the noise.</span>
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.75 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="w-full text-center text-xs md:text-sm text-gray-500 tracking-[0.2em] font-light uppercase"
            >
              Under construction.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="pt-10"
          >
            <button 
              onClick={scrollToVideos}
              className="px-10 py-4 rounded-full bg-white/5 border border-white/10 text-white hover:border-cyan-400/50 transition-all duration-500"
            >
              Clarity in Chaos Videos
            </button>
          </motion.div>
        </motion.div>

        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2 text-gray-600 cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          onClick={scrollToVideos}
        >
          <FiArrowDown size={28} />
        </motion.div>
      </section>

      {/* Videos Grid Section */}
      <section id="videos" className="py-32 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24 text-center">
            <GlowText as="h2" className="text-4xl md:text-6xl text-white mb-6" color="cyan">
              Latest Videos
            </GlowText>
          </div>

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 lg:grid-cols-3 gap-10"
              >
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-video bg-white/[0.02] rounded-2xl animate-pulse" />
                ))}
              </motion.div>
            ) : error ? (
              <motion.div 
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-20 text-center"
              >
                <FiAlertCircle className="text-red-500/50 text-5xl mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Videos are temporarily unavailable</h3>
                <button 
                  onClick={loadVideos}
                  className="mt-4 flex items-center space-x-2 px-6 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <FiRefreshCw className="text-cyan-400" />
                  <span>Retry Connection</span>
                </button>
              </motion.div>
            ) : (
              <>
                <motion.div 
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-10"
                >
                  {videos.map((video) => (
                    <VideoCard key={video.videoId} video={video} />
                  ))}
                </motion.div>

                {/* View All Button */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="mt-20 flex justify-center"
                >
                  <a 
                    href="https://www.youtube.com/@universalvision00/videos" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-8 py-3.5 rounded-full bg-white/5 border border-white/10 text-white hover:border-cyan-400/30 hover:bg-white/[0.07] hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] transition-all duration-500 flex items-center space-x-3 text-sm font-medium tracking-wide"
                  >
                    <span>View All Videos on YouTube</span>
                    <span className="text-cyan-400 text-lg">→</span>
                  </a>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;